import { useMemo } from 'react'
import {
  Chip,
  Menu,
  MenuItem,
  ListItemText,
  Checkbox,
} from '@mui/material'
import { ExpandMore } from '@mui/icons-material'
import { chickenBreedOptions, duckBreedOptions, speciesOptions } from '../constants/EntryFormOptions'

const FilterChip = ({ 
  filterType, 
  selectedValues, 
  setSelectedValues, 
  menuAnchor, 
  setMenuAnchor, 
  entries = [],
  label,
  onMenuOpen
}) => {

  // Get filter options based on filter type
  const filterOptions = useMemo(() => {
    switch (filterType) {
      case 'breed': {
        // Handle breed as both array and string for backwards compatibility
        const allBreeds = entries.flatMap(entry => {
          if (Array.isArray(entry.breed)) {
            return entry.breed
          } else if (entry.breed) {
            return [entry.breed]
          }
          return []
        })
        const breedValues = [...new Set(allBreeds)].filter(Boolean).sort()
        return breedValues.map(value => ({
          value,
          label: value
        }))
      }
      case 'species': {
        const speciesValues = [...new Set(entries.map(entry => entry.species).filter(Boolean))].sort()
        return speciesValues.map(value => ({
          value,
          label: value
        }))
      }
      case 'mother': {
        const motherValues = [...new Set(entries.map(entry => entry.mother).filter(Boolean))].sort()
        return motherValues.map(value => ({ value, label: value }))
      }
      case 'father': {
        const fatherValues = [...new Set(entries.map(entry => entry.father).filter(Boolean))].sort()
        return fatherValues.map(value => ({ value, label: value }))
      }
      default:
        return []
    }
  }, [entries, filterType])

  const handleChipClick = (event) => {
    // Close other menus first if callback provided
    if (onMenuOpen) {
      onMenuOpen(filterType)
    }
    setMenuAnchor(event.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
  }

  const handleClearAll = () => {
    setSelectedValues([])
  }

  const handleToggleOption = (value) => {
    setSelectedValues(prev => 
      prev.includes(value) 
        ? prev.filter(v => v !== value)
        : [...prev, value]
    )
  }

  const chipLabel = selectedValues.length > 0 
    ? `${label} (${selectedValues.length})` 
    : label

  return (
    <>
      <Chip
        label={chipLabel}
        variant={selectedValues.length > 0 ? 'filled' : 'outlined'}
        clickable
        deleteIcon={selectedValues.length > 0 ? <ExpandMore /> : <ExpandMore />}
        onDelete={selectedValues.length > 0 ? handleClearAll : undefined}
        onClick={handleChipClick}
        sx={{ minWidth: 80 }}
      />
      
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleClearAll}>
          <Checkbox checked={selectedValues.length === 0} />
          <ListItemText primary={`All ${label}s`} />
        </MenuItem>
        {filterOptions.map((option) => (
          <MenuItem 
            key={option.value}
            onClick={() => handleToggleOption(option.value)}
          >
            <Checkbox checked={selectedValues.includes(option.value)} />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default FilterChip